import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Shield, Eye, EyeOff, Loader2, Users } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";

const signupSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Must contain at least one uppercase letter")
    .regex(/[0-9]/, "Must contain at least one number"),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignupForm = z.infer<typeof signupSchema>;

export default function SignupPage() {
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const params = new URLSearchParams(window.location.search);
  const errorParam = params.get("error");
  const invitationToken = params.get("invitationToken");
  const invitedEmail = params.get("email") || "";
  const selectedPlan = params.get("plan") || "";

  const form = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: { fullName: "", email: invitedEmail, password: "", confirmPassword: "" },
  });

  async function onSubmit(data: SignupForm) {
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName: data.fullName, email: data.email, password: data.password }),
        credentials: "include",
      });
      const json = await res.json();
      if (!res.ok) {
        toast({ title: "Signup failed", description: json.error?.message || json.message || "Something went wrong. Please try again.", variant: "destructive" });
        return;
      }
      if (json.requiresVerification) {
        sessionStorage.setItem("pendingVerifyEmail", json.email);
        if (invitationToken) {
          sessionStorage.setItem("pendingInvitationToken", invitationToken);
        }
        let verifyUrl = `/verify-email?email=${encodeURIComponent(json.email)}`;
        if (invitationToken) verifyUrl += `&invitationToken=${invitationToken}`;
        if (selectedPlan) verifyUrl += `&plan=${selectedPlan}`;
        setLocation(verifyUrl);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      const dest = invitationToken
        ? `/accept-invitation/${invitationToken}`
        : selectedPlan ? `/choose-plan?plan=${selectedPlan}` : "/choose-plan";
      setLocation(dest);
    } catch {
      toast({ title: "Signup failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  function handleGoogle() {
    if (invitationToken) localStorage.setItem('pendingInvitationToken', invitationToken);
    window.location.href = "/api/auth/google";
  }

  // DISABLED 2026-09-18: Microsoft/Entra sign-in commented out (not deleted).
  // Why: the Entra Domain Services resource was deleted to stop ~$300/mo of spend and
  // telemetry showed zero Microsoft logins. Backend routes are commented out too
  // (server/auth.ts), so this button would 404. Restore both sides together.
  // function handleMicrosoft() {
  // if (invitationToken) localStorage.setItem('pendingInvitationToken', invitationToken);
  // window.location.href = "/api/auth/microsoft";
  // }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center space-x-3 mb-10">
            <div className="rounded-xl p-2" style={{ backgroundColor: "var(--restore-accent)" }}>
              <Shield className="w-6 h-6 text-gray-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">RestoreAI</h1>
              <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>AI-Powered Restoration Intelligence</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-2">Create your account</h2>
          <p className="mb-6" style={{ color: "var(--restore-text-muted)" }}>
            Already have an account?{" "}
            <Link href={invitationToken ? `/login?returnTo=/accept-invitation/${invitationToken}` : "/login"} className="font-medium hover:underline" style={{ color: "var(--restore-accent)" }}>
              Sign in
            </Link>
          </p>

          {invitationToken && invitedEmail && (
            <div className="mb-6 p-4 rounded-xl border border-green-500/30 bg-green-500/10 flex items-start gap-3">
              <Users className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-300">You've been invited to join a team</p>
                <p className="text-xs text-green-400 mt-0.5">
                  Sign up with <span className="font-medium text-white">{invitedEmail}</span> and you'll be added automatically.
                </p>
              </div>
            </div>
          )}

          {/* DISABLED 2026-09-18: Microsoft/Entra sign-in error banner commented out (not
              deleted). Why: Entra Domain Services was deleted to stop ~$300/mo of spend
              and telemetry showed zero Microsoft logins. See server/auth.ts.

            {(errorParam === "microsoft_failed" || errorParam === "microsoft_not_configured") && (
              <div className="mb-6 p-4 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">
                Microsoft sign-in failed. Please try again or use email and password.
              </div>
            )}
          */}

          {errorParam === "google_failed" && (
            <div className="mb-6 p-4 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">
              Google sign-in failed. Please try again or use email and password.
            </div>
          )}

          <div className="space-y-3 mb-6">
            <Button
              type="button"
              onClick={handleGoogle}
              variant="outline"
              className="w-full flex items-center justify-center gap-3 border-gray-600 text-white hover:bg-gray-800 font-medium h-11"
              style={{ backgroundColor: "transparent" }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </Button>
          {/* DISABLED 2026-09-18: Microsoft/Entra sign-in button commented out (not
              deleted). Why: Entra Domain Services was deleted to stop ~$300/mo of spend
              and telemetry showed zero Microsoft logins. See server/auth.ts.

              <Button
                type="button"
                onClick={handleMicrosoft}
                variant="outline"
                className="w-full flex items-center justify-center gap-3 border-gray-600 text-white hover:bg-gray-800 font-medium h-11"
                style={{ backgroundColor: "transparent" }}
              >
                <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
                  <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
                  <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
                  <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                </svg>
                Continue with Microsoft
              </Button>
          */}
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 text-gray-500" style={{ backgroundColor: "var(--restore-dark-bg)" }}>or sign up with email</span>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Full name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Jane Smith"
                        autoComplete="name"
                        className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Email address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        readOnly={!!invitationToken}
                        className={`bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 ${invitationToken ? "opacity-75 cursor-not-allowed" : ""}`}
                      />
                    </FormControl>
                    {invitationToken && (
                      <p className="text-xs text-gray-500 mt-1">Email is set by your invitation and cannot be changed.</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Min. 8 characters"
                          autoComplete="new-password"
                          className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Confirm password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showConfirm ? "text" : "password"}
                          placeholder="Re-enter your password"
                          autoComplete="new-password"
                          className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                        >
                          {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full text-gray-900 font-semibold h-11 text-base"
                style={{ backgroundColor: "var(--restore-accent)" }}
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
              </Button>

              <p className="text-xs text-center" style={{ color: "var(--restore-text-muted)" }}>
                By creating an account you agree to our Terms of Service and Privacy Policy.
                Your account will be reviewed before gaining access.
              </p>
            </form>
          </Form>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-12 border-l border-gray-800">
        <div className="max-w-md text-center">
          <div className="text-6xl font-bold mb-4" style={{ color: "var(--restore-accent)" }}>-7</div>
          <h3 className="text-2xl font-bold text-white mb-2">Days</h3>
          <p className="text-lg font-semibold text-white mb-4">Faster Claim Approval</p>
          <p style={{ color: "var(--restore-text-muted)" }}>
            Join restoration professionals using AI to produce carrier-ready scopes in minutes — not days.
          </p>
        </div>
      </div>
    </div>
  );
}
